/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import Flatpickr from 'react-flatpickr';
import classNames from 'classnames';
import FeatherIcon from 'feather-icons-react';
import { useQuery } from 'react-query';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Russian } from 'flatpickr/dist/l10n/ru.js';
import { UzbekLatin } from 'flatpickr/dist/l10n/uz_latn.js';
import { useSearchParams } from 'react-router-dom';

import Button from 'components/base/Button';
import legalEntityService from 'service/legalEntity';
import warehouseService from 'service/warehouse';
import legalEntityCurrencyService from 'service/legalEntityCurrency';
import paymentOrderService from 'service/paymentOrder';
import invoiceTypeService from 'service/invoiceType';
import reportInvoiceService from 'service/reportInvoice';
import { useWindowWidth } from 'utils/useWindowSize';
import '../styles.scss';

interface FilterProps {
  setDateFrom?: any;
  setDateTo?: any;
  dateFrom?: any;
  dateTo?: any;
  setOptionSenderId?: any;
  setWarehouseId?: any;
  setCurrencyId?: any;
  setTypeId?: any;
  setPaymentId?: any;
  paymentId?: any;
  optionSenderId?: any;
  warehouseId?: any;
  currencyId?: any;
  typeId?: any;
  setCurrentPage?: any;
  setFilialId?: any;
  filialId?: any;
  labeledVisibleFields?: any;
}

const Filter = ({
  setDateFrom,
  setDateTo,
  dateFrom,
  dateTo,
  setOptionSenderId,
  setWarehouseId,
  setCurrencyId,
  setTypeId,
  setPaymentId,
  paymentId,
  optionSenderId,
  warehouseId,
  currencyId,
  typeId,
  setCurrentPage,
  setFilialId,
  filialId,
  labeledVisibleFields
}: FilterProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { t, i18n } = useTranslation();
  const { control, setValue } = useForm();
  const url = process.env.REACT_APP_CDN_URL;
  const windowSize: any = useWindowWidth();

  const updateSearchParams = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value); // Set new value
    } else {
      newParams.delete(key); // Remove if empty
    }
    setSearchParams(newParams);
  };

  const [legalEntityOption, setLegalEntityOption] = useState<any>([]);
  const [warehouseOption, setWarehouseOption] = useState<any>([]);
  const [currencyOption, setCurrencyOption] = useState<any>([]);
  const [paymentOrderOption, setPaymentOrderOption] = useState<any>([]);
  const [invoiceTypeOption, setInvoiceTypeOption] = useState<any>([]);
  const [filialOption, setFilialOption] = useState<any>([]);
  const [loading, setLoading] = useState(false);

  // Initialize today's date in milliseconds
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const todayEnd = new Date().setHours(0, 0, 0, 0);

  useEffect(() => {
    if (!dateFrom) {
      setDateFrom(todayStart);
    }
    if (!dateTo) {
      setDateTo(todayEnd);
    }
  }, [dateFrom, dateTo, setDateFrom, setDateTo]);

  //GET LEgal Entity to options
  useQuery(['GET_BRANCH'], async () => {
    await legalEntityService
      .getListBranch({ offset: 0, limit: 100 })
      .then((res: any) => {
        const options = res?.legal_entities?.map((option: any) => ({
          value: option.pid,
          label: option.name
        }));
        setFilialOption([{ value: 'all', label: t('all') }, ...options]);
      });
  });

  //GET LEgal Entity to options
  useQuery(['GET_LEGAL_ENTITY'], async () => {
    await legalEntityService
      .getLegalEntityOption({ offset: 0, limit: 100 })
      .then((res: any) => {
        const options = res?.legal_entities?.map((option: any) => ({
          value: option.pid,
          label: option.name
        }));
        setLegalEntityOption(options);
      });
  });

  //GET Warehouse Entity to options
  useQuery(['GET_WAREHOUSE'], async () => {
    await warehouseService
      .getList({ offset: 0, limit: 100 })
      .then((res: any) => {
        const options = res?.ware_houses?.map((option: any) => ({
          value: option.pid,
          label: option.name
        }));
        setWarehouseOption(options);
      });
  });

  //GET Currency Entity to options
  useQuery(['GET_CURRENCY'], async () => {
    await legalEntityCurrencyService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.legal_entity_currencies?.map((option: any) => ({
          value: option?.currency?.pid,
          label: option?.currency?.name?.[i18n?.language]
        }));
        setCurrencyOption(options);
      });
  });

  //GET Payment Order Entity to options
  useQuery(['GET_PAYMENT_ORDER'], async () => {
    await paymentOrderService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.payment_orders?.map((option: any) => ({
          value: option?.pid,
          label: option?.name?.[i18n?.language]
        }));
        setPaymentOrderOption(options);
      });
  });

  //GET Invoice Type to options
  useQuery(['GET_INVOICE_TYPE'], async () => {
    await invoiceTypeService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.invoice_types
          ?.filter((items: any) => items?.status)
          .map((option: any) => ({
            value: option?.pid,
            label: option?.name?.[i18n?.language]
          }));
        setInvoiceTypeOption(options);
      });
  });

  const handleStartDateChange = (date: Date | Date[]) => {
    if (Array.isArray(date)) {
      const startDate = new Date(date[0]);
      startDate.setHours(0, 0, 0, 0); // Set time to 00:00
      const startDateInMillis = startDate.getTime();
      setDateFrom(startDateInMillis);
      updateSearchParams('dateFrom', startDateInMillis?.toString());
    } else {
      const startDate = new Date(date);
      startDate.setHours(0, 0, 0, 0); // Set time to 00:00
      const startDateInMillis = startDate.getTime();
      setDateFrom(startDateInMillis);
      updateSearchParams('dateFrom', startDateInMillis?.toString());
    }
  };

  const handleEndDateChange = (date: Date | Date[]) => {
    if (Array.isArray(date)) {
      const endDateInMillis = date[0]?.getTime();
      setDateTo(endDateInMillis);
      updateSearchParams('dateTo', endDateInMillis?.toString());
    } else {
      const endDateInMillis = date.getTime();
      setDateTo(endDateInMillis);
      updateSearchParams('dateTo', endDateInMillis?.toString());
    }
  };

  const getFlatpickrLocale = () => {
    switch (i18n.language) {
      case 'ru':
        return Russian;
      case 'uz':
        return UzbekLatin;
      default:
        return undefined;
    }
  };

  const handleExport = () => {
    setLoading(true);
    const customFilename: string = 'Закуп по накладной.xlsx';

    // Calculate adjusted dates with defaults
    const adjustedDateFrom = dateFrom || todayStart;
    /* prettier-ignore */
    const adjustedDateTo =
      !dateFrom && !dateTo
        ? todayEnd + 86400000
        : dateTo === dateFrom
          ? dateFrom + 86400000
          : dateTo
            ? dateTo + 86400000
            : undefined;

    // Prepare request payload
    const requestData = {
      date_from: adjustedDateFrom,
      date_to: adjustedDateTo,
      supplier_pid: optionSenderId,
      payment_order_pid: paymentId,
      invoice_type_pid: typeId,
      currency_pid: currencyId,
      warehouse_pid: warehouseId,
      branch_id: filialId,
      visible_columns: labeledVisibleFields
    };

    // Fetch report and trigger download
    reportInvoiceService
      ?.getListExcel(requestData)
      .then((res: any) => {
        setLoading(false);
        if (res?.filename !== '') {
          const resUrl = `${url}${res?.filename}`;
          const link = document.createElement('a');
          link.href = resUrl;
          link.download = customFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      })
      .catch(error => {
        setLoading(false);
        console.log('error==>', error);
      });
  };

  useEffect(() => {
    const providerParam = searchParams.get('provider') || 0;
    const stockParam = searchParams.get('stock') || 0;
    const currencyParam = searchParams.get('currency') || 0;
    const paymentParam = searchParams.get('payment') || 0;
    const typeParam = searchParams.get('type') || 0;
    const filialParam = searchParams.get('branch') || 0;
    const dateFromParam = searchParams.get('dateFrom');
    const dateToParam = searchParams.get('dateTo');
    const pageParam = searchParams.get('pageParam');

    if (pageParam) {
      setCurrentPage(Number(pageParam));
    }

    if (dateFromParam) {
      setDateFrom(Number(dateFromParam));
    }
    if (dateToParam) {
      setDateTo(Number(dateToParam));
    }

    setOptionSenderId(providerParam);
    setWarehouseId(stockParam);
    setCurrencyId(currencyParam);
    setPaymentId(paymentParam);
    setTypeId(typeParam);
    setFilialId(filialParam);

    // Sync with react-hook-form values
    setValue('provider', providerParam);
    setValue('stock', stockParam);
    setValue('currency', currencyParam);
    setValue('payment_order', paymentParam);
    setValue('type', typeParam);
    setValue('branch', filialParam);
  }, [searchParams, setValue]);

  return (
    <>
      <Form>
        <div className="new-rows">
          <div className="d-flex gap-2">
            <Form.Group className="md-4 w-100">
              <div className="react-select-container">
                <Controller
                  name="branch"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Form.Floating>
                      <Form.Select
                        {...field}
                        onChange={(e: any) => {
                          const selectedOption = filialOption.find(
                            (option: any) =>
                              Number(option.value) === Number(e.target.value)
                          );
                          if (selectedOption) {
                            setFilialId(selectedOption?.value);
                            updateSearchParams('branch', selectedOption?.value); // Update searchParams
                          }
                          field.onChange(e);
                        }}
                      >
                        <option className="d-none" value=""></option>
                        {filialOption?.map((option: any) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>

                      <label htmlFor="floatingInputCustom">{t('branch')}</label>
                      {filialId !== 0 && (
                        <Button
                          variant="link"
                          className="close-btn p-0"
                          onClick={() => {
                            setFilialId(null);
                            setValue('branch', '');
                            updateSearchParams('branch', '');
                          }}
                        >
                          <FeatherIcon icon="delete" size={16} />
                        </Button>
                      )}
                    </Form.Floating>
                  )}
                />
              </div>
            </Form.Group>

            <Form.Group className="md-4 w-100">
              <div className="react-select-container">
                <Controller
                  name="provider"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Form.Floating>
                      <Form.Select
                        {...field}
                        onChange={(e: any) => {
                          const selectedOption = legalEntityOption.find(
                            (option: any) =>
                              Number(option.value) === Number(e.target.value)
                          );
                          if (selectedOption) {
                            setOptionSenderId(selectedOption?.value);
                            updateSearchParams(
                              'provider',
                              selectedOption?.value
                            ); // Update searchParams
                          }
                          field.onChange(e);
                        }}
                      >
                        <option className="d-none" value=""></option>
                        {legalEntityOption?.map((option: any) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>

                      <label htmlFor="floatingInputCustom">{t('users')}</label>
                      {optionSenderId !== 0 && (
                        <Button
                          variant="link"
                          className="close-btn p-0"
                          onClick={() => {
                            setOptionSenderId(null);
                            setValue('provider', '');
                            updateSearchParams('provider', '');
                          }}
                        >
                          <FeatherIcon icon="delete" size={16} />
                        </Button>
                      )}
                    </Form.Floating>
                  )}
                />
              </div>
            </Form.Group>
          </div>

          <div className="d-flex gap-2">
            <Form.Group className="md-4 w-100">
              <div className="react-select-container">
                <Controller
                  name="stock"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Form.Floating>
                      <Form.Select
                        {...field}
                        onChange={(e: any) => {
                          const selectedOption = warehouseOption.find(
                            (option: any) =>
                              Number(option.value) === Number(e.target.value)
                          );
                          if (selectedOption) {
                            setWarehouseId(selectedOption?.value);
                            updateSearchParams('stock', selectedOption?.value);
                          }
                          field.onChange(e);
                        }}
                      >
                        <option className="d-none" value=""></option>
                        {warehouseOption?.map((option: any) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>

                      <label htmlFor="floatingInputCustom">
                        {t('warehouse')}
                      </label>
                      {warehouseId !== 0 && (
                        <Button
                          variant="link"
                          className="close-btn p-0"
                          onClick={() => {
                            setWarehouseId(null);
                            setValue('stock', '');
                            updateSearchParams('stock', '');
                          }}
                        >
                          <FeatherIcon icon="delete" size={16} />
                        </Button>
                      )}
                    </Form.Floating>
                  )}
                />
              </div>
            </Form.Group>
            <Form.Group className="md-4 w-100">
              <div className="react-select-container">
                <Controller
                  name="currency"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Form.Floating>
                      <Form.Select
                        {...field}
                        onChange={(e: any) => {
                          const selectedOption = currencyOption.find(
                            (option: any) =>
                              Number(option.value) === Number(e.target.value)
                          );
                          if (selectedOption) {
                            setCurrencyId(selectedOption?.value);
                            updateSearchParams(
                              'currency',
                              selectedOption?.value
                            );
                          }
                          field.onChange(e);
                        }}
                      >
                        <option className="d-none" value=""></option>
                        {currencyOption?.map((option: any) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>

                      <label htmlFor="floatingInputCustom">
                        {t('currency')}
                      </label>
                      {currencyId !== 0 && (
                        <Button
                          variant="link"
                          className="close-btn p-0"
                          onClick={() => {
                            setCurrencyId(null);
                            setValue('currency', '');
                            updateSearchParams('currency', '');
                          }}
                        >
                          <FeatherIcon icon="delete" size={16} />
                        </Button>
                      )}
                    </Form.Floating>
                  )}
                />
              </div>
            </Form.Group>
          </div>

          <div className="d-flex gap-2">
            <Form.Group className="md-4 w-100">
              <div className="react-select-container">
                <Controller
                  name="type"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Form.Floating>
                      <Form.Select
                        {...field}
                        onChange={(e: any) => {
                          const selectedOption = invoiceTypeOption.find(
                            (option: any) =>
                              Number(option.value) === Number(e.target.value)
                          );
                          if (selectedOption) {
                            setTypeId(selectedOption?.value);
                            updateSearchParams('type', selectedOption?.value);
                          }
                          field.onChange(e);
                        }}
                      >
                        <option className="d-none" value=""></option>
                        {invoiceTypeOption?.map((option: any) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>

                      <label htmlFor="floatingInputCustom">{t('type')}</label>
                      {typeId !== 0 && (
                        <Button
                          variant="link"
                          className="close-btn p-0"
                          onClick={() => {
                            setTypeId(null);
                            setValue('type', '');
                            updateSearchParams('type', '');
                          }}
                        >
                          <FeatherIcon icon="delete" size={16} />
                        </Button>
                      )}
                    </Form.Floating>
                  )}
                />
              </div>
            </Form.Group>
            <Form.Group className="md-4 w-100">
              <div className="react-select-container">
                <Controller
                  name="payment_order"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Form.Floating>
                      <Form.Select
                        {...field}
                        onChange={(e: any) => {
                          const selectedOption = paymentOrderOption.find(
                            (option: any) =>
                              Number(option.value) === Number(e.target.value)
                          );
                          if (selectedOption) {
                            setPaymentId(selectedOption?.value);
                            updateSearchParams(
                              'payment',
                              selectedOption?.value
                            );
                          }
                          field.onChange(e);
                        }}
                      >
                        <option className="d-none" value=""></option>
                        {paymentOrderOption?.map((option: any) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>

                      <label htmlFor="floatingInputCustom">
                        {t('payment_procedure')}
                      </label>
                      {paymentId !== 0 && (
                        <Button
                          variant="link"
                          className="close-btn p-0"
                          onClick={() => {
                            setPaymentId(null);
                            setValue('payment_order', '');
                            updateSearchParams('payment', '');
                          }}
                        >
                          <FeatherIcon icon="delete" size={16} />
                        </Button>
                      )}
                    </Form.Floating>
                  )}
                />
              </div>
            </Form.Group>
          </div>

          <div className="d-flex gap-2 align-items-center justify-content-between">
            <Form.Group className="md-4 w-100">
              <Form.Floating>
                <Flatpickr
                  className={classNames('form-control')}
                  options={{
                    nextArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>`,
                    prevArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M224 480c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25l192-192c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l169.4 169.4c12.5 12.5 12.5 32.75 0 45.25C240.4 476.9 232.2 480 224 480z"/></svg>`,
                    locale: getFlatpickrLocale(),
                    monthSelectorType: 'static',
                    onDayCreate: (...[, , , dayElem]) => {
                      if (
                        dayElem.dateObj.getDay() === 5 ||
                        dayElem.dateObj.getDay() === 6
                      ) {
                        dayElem.className += ' weekend-days';
                      }
                    },
                    dateFormat: 'M j, Y',
                    disableMobile: true
                  }}
                  value={dateFrom}
                  onChange={handleStartDateChange}
                  placeholder={t('date_from')}
                />
                <label htmlFor="floatingInputCustom">{t('date_from')}</label>
              </Form.Floating>
            </Form.Group>

            <Form.Group className="md-4 w-100">
              <Form.Floating>
                <Flatpickr
                  className={classNames('form-control')}
                  options={{
                    nextArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>`,
                    prevArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M224 480c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25l192-192c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l169.4 169.4c12.5 12.5 12.5 32.75 0 45.25C240.4 476.9 232.2 480 224 480z"/></svg>`,
                    locale: getFlatpickrLocale(),
                    monthSelectorType: 'static',
                    onDayCreate: (...[, , , dayElem]) => {
                      if (
                        dayElem.dateObj.getDay() === 5 ||
                        dayElem.dateObj.getDay() === 6
                      ) {
                        dayElem.className += ' weekend-days';
                      }
                    },
                    dateFormat: 'M j, Y',
                    disableMobile: true
                  }}
                  value={dateTo}
                  onChange={handleEndDateChange}
                  placeholder={t('date_to')}
                />
                <label htmlFor="floatingInputCustom">{t('date_to')}</label>
              </Form.Floating>
            </Form.Group>
            <div className="d-flex justify-content-between gap-2">
              <Button
                className="d-flex align-items-center gap-2"
                style={{ height: '48px' }}
                variant="outline-primary"
                onClick={handleExport}
                loading={loading}
                loadingPosition="start"
              >
                <FeatherIcon icon="upload-cloud" size={20} />
                {windowSize > 768 && t('export')}
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </>
  );
};

export default Filter;
